import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib/utils';
import { useIndicator } from '../theme/hooks';
import { DAY_MS, dayLabel, isoDay, parseDay, rangeLabel } from './api';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MAX_DAYS = 366;

type Range = { from: string; to: string };

const presets = (today: number): [string, number][] => {
  const d = new Date(today);
  return [
    ['Last 7 days', today - 6 * DAY_MS],
    ['Last 30 days', today - 29 * DAY_MS],
    ['Last 90 days', today - 89 * DAY_MS],
    ['This month', Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)],
    ['Year to date', Date.UTC(d.getUTCFullYear(), 0, 1)],
    ['Last 12 months', today - 364 * DAY_MS],
  ];
};

const monthOf = (t: number) => {
  const d = new Date(t);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};

/**
 * Date range button + popover (bottom sheet on phones), ported from the Splitfin prototype:
 * presets with a sliding highlight, a month calendar with range fill and hover preview,
 * and a label that rolls in on apply.
 */
export const RangePicker: React.FC<{ value: Range; today: string; onApply: (r: Range) => void; host: HTMLElement | null }> = ({ value, today, onApply, host }) => {
  const TODAY = parseDay(today);
  const PRE = presets(TODAY);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(parseDay(value.from));
  const [end, setEnd] = useState<number | null>(parseDay(value.to));
  const [hover, setHover] = useState<number | null>(null);
  const [view, setView] = useState(monthOf(parseDay(value.to)));
  const [slide, setSlide] = useState<{ dir: 'l' | 'r'; n: number } | null>(null);
  const [rollKey, setRollKey] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);

  const matchPreset = end === TODAY ? PRE.findIndex(([, s]) => s === start) : -1;
  const pre = useIndicator<HTMLDivElement>('button[aria-pressed="true"]', [matchPreset, open], true);

  const openPicker = (o: boolean) => {
    if (o) {
      setStart(parseDay(value.from));
      setEnd(parseDay(value.to));
      setView(monthOf(parseDay(value.to)));
      setSlide(null);
    }
    setOpen(o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (t.isConnected && wrap.current && !wrap.current.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const goMonth = (target: number) => {
    if (target === view) return;
    setSlide((s) => ({ dir: target > view ? 'l' : 'r', n: (s?.n ?? 0) + 1 }));
    setView(target);
  };

  const pickDay = (t: number) => {
    if (end === null) {
      const a = Math.min(start, t);
      const b = Math.max(start, t);
      // Keep the range inside what the API accepts.
      setStart(Math.max(a, b - (MAX_DAYS - 1) * DAY_MS));
      setEnd(b);
    } else {
      setStart(t);
      setEnd(null);
    }
    setHover(null);
  };

  const apply = () => {
    const e = end ?? start;
    onApply({ from: isoDay(start), to: isoDay(e) });
    setRollKey((k) => k + 1);
    setOpen(false);
    btn.current?.focus();
  };

  const v = new Date(view);
  const y0 = v.getUTCFullYear();
  const m0 = v.getUTCMonth();
  const first = (new Date(Date.UTC(y0, m0, 1)).getUTCDay() + 6) % 7;
  const len = new Date(Date.UTC(y0, m0 + 1, 0)).getUTCDate();
  const e = end ?? hover;
  const lo = e != null ? Math.min(start, e) : start;
  const hi = e != null ? Math.max(start, e) : start;

  const picker = (
    <div ref={wrap} className="relative">
      <button ref={btn} type="button" className="oa-accent-btn" aria-haspopup="dialog" aria-expanded={open} onClick={() => openPicker(!open)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M8 3v4M16 3v4M3 10h18" />
        </svg>
        <span className="oa-roll">
          <span key={rollKey}>{rangeLabel(value.from, value.to)}</span>
        </span>
        <svg className="oa-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div className={cn('oa-pop', open && 'is-open')} role="dialog" aria-label="Choose a date range" aria-hidden={!open}>
        <div ref={pre.ref} className="oa-presets">
          <span className="oa-ind" style={pre.style} aria-hidden="true" />
          {PRE.map(([label, s], i) => (
            <button
              key={label}
              type="button"
              aria-pressed={i === matchPreset}
              tabIndex={open ? 0 : -1}
              onClick={() => {
                setStart(s);
                setEnd(TODAY);
                goMonth(monthOf(TODAY));
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-center justify-between font-semibold">
            <button type="button" className="oa-icon-btn is-sm is-bordered" aria-label="Previous month" tabIndex={open ? 0 : -1} onClick={() => goMonth(Date.UTC(y0, m0 - 1, 1))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="m15 6-6 6 6 6" />
              </svg>
            </button>
            <span className="font-montserrat text-[14px]">
              {MONTHS[m0]} {y0}
            </span>
            <button type="button" className="oa-icon-btn is-sm is-bordered disabled:opacity-30" aria-label="Next month" tabIndex={open ? 0 : -1} disabled={Date.UTC(y0, m0 + 1, 1) > TODAY} onClick={() => goMonth(Date.UTC(y0, m0 + 1, 1))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
          </div>
          <div className="oa-dow">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>
          <div key={slide?.n ?? 0} className={cn('oa-days', slide && (slide.dir === 'l' ? 'is-slide-l' : 'is-slide-r'))} onMouseLeave={() => end === null && setHover(null)}>
            {Array.from({ length: first }, (_, i) => (
              <span key={`b${i}`} />
            ))}
            {Array.from({ length: len }, (_, i) => {
              const t = Date.UTC(y0, m0, i + 1);
              return (
                <button
                  key={t}
                  type="button"
                  tabIndex={open ? 0 : -1}
                  className={cn('oa-day', t >= lo && t <= hi && 'is-in', t === lo && 'is-rs', t === hi && 'is-re', t === TODAY && 'is-today')}
                  disabled={t > TODAY}
                  aria-label={dayLabel(t, true)}
                  onClick={() => pickDay(t)}
                  onMouseEnter={() => end === null && t !== hover && setHover(t)}
                >
                  <span>{i + 1}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-[var(--oa-line)] pt-2.5">
            <span className="oa-cap">
              {end === null ? 'Pick an end date' : `${Math.round(((end ?? start) - start) / DAY_MS) + 1} days, compared with the ${Math.round(((end ?? start) - start) / DAY_MS) + 1} before`}
            </span>
            <button type="button" className="oa-apply" tabIndex={open ? 0 : -1} onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      </div>
      <div className={cn('oa-scrim', open && 'is-open')} aria-hidden="true" onClick={() => setOpen(false)} />
    </div>
  );

  return host ? createPortal(picker, host) : null;
};
